import React, { memo, useCallback } from "react";
import { Col, Row } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";

import {
	Button,
	FormControlLabel,
	GenericSwitch,
	HookSelectFormGroup,
	Skeleton,
} from "components";
import { AriseFeesTemplateFields } from "globalConstants";
import { useLoader } from "hooks";
import { useCheckAriseFeeTemplateNameMutation } from "queries";
import { FeeTemplatesStatusTypes } from "types";

import FeeTemplateField from "../common/feeTemplateField";
import FeeTemplateNameField from "../common/feeTemplateNameField";

import styles from "./ariseFeeTemplateForm.module.scss";

type AchFeeTemplateFormProps = {
	companyId: string;
	defaultValues?: { [key: string]: any };
	isLoading?: boolean;
	linkToListing: string;
	isSingle: boolean;
	isEdit?: boolean;
	onSubmit: Function;
};

const {
	IsDefault,
	Status,
	TemplateName,
	PlatformSetupFee,
	PlatformMonthlyFee,
	CustomerVaultMonthlyFee,
	EnhancedDataTransactionFee,
	CardTransactionAuthorizationFee,
	ECommercePluginSetupFee,
	ECommercePluginMonthlyFee,
	EMVDeviceSetupFee,
	AdditionalEmvDeviceMonthlyFee,
	EnhancedDataSetupFee,
	CustomerVaultSetupFee,
	CustomerVaultAutomaticUpdateFee,
	MobileInvoicingPaymentsSetupFee,
	MobileInvoicingPaymentsMonthlyFee,
	MobileInvoicingPaymentsMessageFee,
	CardNetworkTokenizationToggle,
	CardNetworkTokenizationSetupFee,
	CardNetworkTokenizationMonthlyFee,
	CardNetworkTokenizationPerItemFee,
} = AriseFeesTemplateFields;

const statusOptions = FeeTemplatesStatusTypes.getOptions();

const AchFeeTemplateForm = ({
	companyId,
	defaultValues = {},
	isLoading,
	linkToListing,
	isSingle,
	isEdit,
	onSubmit,
}: AchFeeTemplateFormProps) => {
	const history = useHistory();

	const Loader = useLoader(isLoading);

	const formMethods = useForm({
		mode: "onChange",
		reValidateMode: "onChange",
		values: defaultValues,
	});

	const {
		register,
		watch,
		handleSubmit,
		setValue,
		formState: { isSubmitting },
	} = formMethods;

	const handleStatusChange = useCallback(
		fieldValue => {
			if (
				fieldValue.value === FeeTemplatesStatusTypes.Unavailable.value
			) {
				setValue(IsDefault.name, false);
			}
		},
		[setValue],
	);

	const handleSubmitForm = useCallback(
		formValues => {
			onSubmit(
				isEdit
					? formValues
					: {
							...formValues,
							[Status.name]: FeeTemplatesStatusTypes.Available,
					  },
			);
		},
		[isEdit, onSubmit],
	);

	const handleCancel = useCallback(() => {
		history.push(linkToListing);
	}, [history, linkToListing]);

	const isAvailable = !!watch(Status.name)?.value;

	const isDefaultInitial = defaultValues?.[IsDefault.name];

	const FeeTemplateFieldSetup = useCallback(
		props => <FeeTemplateField isLoading={isLoading} {...props} />,
		[isLoading],
	);

	return (
		<div className={styles.container}>
			<FormProvider {...formMethods}>
				<form onSubmit={handleSubmit(handleSubmitForm)}>
					<Row className="pb-4">
						<Col>
							<Loader
								skeleton={<Skeleton height={34} />}
								component={
									<FormControlLabel
										control={
											<GenericSwitch
												register={register}
												name={IsDefault.name}
												id={IsDefault.name}
												isDisabled={
													isSingle ||
													!isAvailable ||
													isDefaultInitial
												}
											/>
										}
										label={IsDefault.displayName}
									/>
								}
							/>
						</Col>
					</Row>
					<Row>
						<Col lg={5}>
							{isEdit && (
								<HookSelectFormGroup
									name={Status.name}
									label={Status.displayName}
									placeholder="Please Select"
									options={statusOptions}
									getOptionLabel={option =>
										option.displayName
									}
									getOptionValue={option => option.value}
									isSearchable={false}
									defaultOptions
									blurLoading={isLoading}
									isDisabled={isSingle || isDefaultInitial}
									onChange={handleStatusChange}
								/>
							)}
						</Col>
						<Col lg={{ span: 5, offset: 2 }}>
							<FeeTemplateNameField
								isLoading={isLoading}
								companyId={companyId}
								fieldInitialValue={
									defaultValues?.[TemplateName.name]
								}
								fieldData={TemplateName}
								useCheckFeeTemplateNameMutation={
									useCheckAriseFeeTemplateNameMutation
								}
							/>
						</Col>
					</Row>
					<hr className={styles.line} />
					<Row>
						<Col lg={5}>
							<FeeTemplateFieldSetup data={PlatformSetupFee} />
						</Col>
						<Col lg={{ span: 5, offset: 2 }}>
							<FeeTemplateFieldSetup data={PlatformMonthlyFee} />
						</Col>
					</Row>
					<Row>
						<Col lg={5}>
							<FeeTemplateFieldSetup
								data={CustomerVaultMonthlyFee}
							/>
						</Col>
						<Col lg={{ span: 5, offset: 2 }}>
							<FeeTemplateFieldSetup
								data={EnhancedDataTransactionFee}
							/>
						</Col>
					</Row>
					<Row>
						<Col lg={5}>
							<FeeTemplateFieldSetup
								data={CardTransactionAuthorizationFee}
							/>
						</Col>
						<Col lg={{ span: 5, offset: 2 }}>
							<FeeTemplateFieldSetup
								data={ECommercePluginSetupFee}
							/>
						</Col>
					</Row>
					<Row>
						<Col lg={5}>
							<FeeTemplateFieldSetup
								data={ECommercePluginMonthlyFee}
							/>
						</Col>
						<Col lg={{ span: 5, offset: 2 }}>
							<FeeTemplateFieldSetup data={EMVDeviceSetupFee} />
						</Col>
					</Row>
					<Row>
						<Col lg={5}>
							<FeeTemplateFieldSetup
								data={AdditionalEmvDeviceMonthlyFee}
							/>
						</Col>
						<Col lg={{ span: 5, offset: 2 }}>
							<FeeTemplateFieldSetup
								data={EnhancedDataSetupFee}
							/>
						</Col>
					</Row>
					<Row>
						<Col lg={5}>
							<FeeTemplateFieldSetup
								data={CustomerVaultSetupFee}
							/>
						</Col>
						<Col lg={{ span: 5, offset: 2 }}>
							<FeeTemplateFieldSetup
								data={CustomerVaultAutomaticUpdateFee}
							/>
						</Col>
					</Row>
					<Row>
						<Col lg={5}>
							<FeeTemplateFieldSetup
								data={MobileInvoicingPaymentsSetupFee}
							/>
						</Col>
						<Col lg={{ span: 5, offset: 2 }}>
							<FeeTemplateFieldSetup
								data={MobileInvoicingPaymentsMonthlyFee}
							/>
						</Col>
					</Row>
					<Row>
						<Col lg={5}>
							<FeeTemplateFieldSetup
								data={MobileInvoicingPaymentsMessageFee}
							/>
						</Col>
					</Row>
					<hr className={styles.line} />
					<Row className="pb-4">
						<Col>
							<Loader
								skeleton={<Skeleton height={34} />}
								component={
									<FormControlLabel
										control={
											<GenericSwitch
												register={register}
												name={
													CardNetworkTokenizationToggle.name
												}
												id={
													CardNetworkTokenizationToggle.name
												}
											/>
										}
										label={
											CardNetworkTokenizationToggle.displayName
										}
									/>
								}
							/>
						</Col>
					</Row>
					<Row>
						<Col lg={3}>
							<FeeTemplateFieldSetup
								data={CardNetworkTokenizationSetupFee}
							/>
						</Col>
						<Col lg={{ span: 3, offset: 1 }}>
							<FeeTemplateFieldSetup
								data={CardNetworkTokenizationMonthlyFee}
							/>
						</Col>
						<Col lg={{ span: 4, offset: 1 }}>
							<FeeTemplateFieldSetup
								data={CardNetworkTokenizationPerItemFee}
							/>
						</Col>
					</Row>

					<Row className={styles.buttonWrapper}>
						<Col>
							<Button
								onClick={handleCancel}
								value="Cancel"
								variant="outline-primary"
							/>
							<Button
								disabled={isSubmitting || isLoading}
								value="Save"
								variant="primary"
								type="submit"
							/>
						</Col>
					</Row>
				</form>
			</FormProvider>
		</div>
	);
};

export default memo(AchFeeTemplateForm);
